import React, { useEffect, useRef, useState } from 'react';
import Topbar from '../components/Topbar';
import Leftbar from '../components/Leftbar';
import { Link } from 'react-router-dom';
import {
  getMaternityUserOverview,
  getMaternityUserReports,
  getMaternityUserAppointments,
  getMaternityUserTasks,
  getMaternityUserWeekTasks,
  deleteMaternityReport,
} from '../Utils/utils';
import { toggleLoader } from '../actions/loader';
import { connect } from 'react-redux';
import moment from 'moment';
import './style.css';

const AVATAR_PALETTE = [
  '#5a4cdb', '#d6336c', '#2b6cb0', '#e8590c', '#0ca678',
  '#7048e8', '#c2255c', '#1098ad', '#f08c00', '#37b24d',
];

const colorForName = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const initialsOf = (name = '') => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return '?';
};

const fmtDate = (d) => (d ? moment(d).format('DD MMM YYYY') : '—');
const fmtDateTime = (d) => (d ? moment(d).format('DD MMM YYYY, hh:mm A') : '—');
const fmtList = (arr) => (Array.isArray(arr) && arr.length ? arr.map((s) => s.replace(/_/g, ' ')).join(', ') : '—');

const InfoRow = ({ label, value }) => (
  <div className="ud-info-row">
    <span className="ud-info-label">{label}</span>
    <span className="ud-info-value">{value != null && value !== '' ? value : '—'}</span>
  </div>
);

const SectionCard = ({ title, icon, children }) => (
  <div className="ud-section-card">
    <div className="ud-section-header">
      <i className={`fas ${icon} ud-section-icon`} />
      <h5 className="ud-section-title">{title}</h5>
    </div>
    <div className="ud-section-body">{children}</div>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="ud-empty">
    <i className="fas fa-inbox" />
    <p>{message || 'No data available'}</p>
  </div>
);

const ChipList = ({ items }) => {
  if (!Array.isArray(items) || !items.length) return <span>—</span>;
  return (
    <div className="ud-chip-list">
      {items.map((item, i) => (
        <span key={i} className="ud-chip">{item.replace(/_/g, ' ')}</span>
      ))}
    </div>
  );
};

const BoolBadge = ({ value }) => (
  <span className={`ud-bool-badge ${value ? 'ud-bool-yes' : 'ud-bool-no'}`}>
    {value ? 'Yes' : 'No'}
  </span>
);

const StatusBadge = ({ value, colorMap }) => {
  const color = (colorMap && colorMap[value]) || '#6b7088';
  return (
    <span className="ud-status-badge" style={{ background: color + '20', color }}>
      {(value || '—').replace(/_/g, ' ')}
    </span>
  );
};

const reportTypeColors = {
  blood_test: '#d6336c',
  mri_scan: '#7048e8',
  x_ray: '#e8590c',
  ultrasound: '#1098ad',
  prescription: '#37b24d',
  scan: '#2b6cb0',
  other: '#6b7088',
};

const appointmentStatusColors = {
  upcoming: '#37b24d',
  completed: '#2b6cb0',
  cancelled: '#d6336c',
};

const taskTypeColors = {
  medication:   { bg: '#fff0f3', color: '#d6336c' },
  self_care:    { bg: '#eafaf0', color: '#37b24d' },
  appointment:  { bg: '#e8f0fe', color: '#2b6cb0' },
  exercise:     { bg: '#fff4e6', color: '#f08c00' },
  nutrition:    { bg: '#f3eeff', color: '#7048e8' },
};

const frequencyColors = {
  daily:      { bg: '#fff4e6', color: '#e8590c' },
  weekly:     { bg: '#e6f9f5', color: '#0ca678' },
  preg_week:  { bg: '#f3eeff', color: '#7048e8' },
};

const JOURNEY_KNOWN_KEYS = [
  '_id', '__v', 'user_id', 'for_whom', 'person_name', 'person_age',
  'journey_stage', 'lmp_date', 'due_date', 'cycle_length', 'consulting_doctor',
  'current_symptoms', 'help_preferences', 'is_completed', 'completed_at',
  'createdAt', 'updatedAt',
];

const formatFieldLabel = (key) => key
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

const formatFieldValue = (value) => {
  if (typeof value === 'boolean') return <BoolBadge value={value} />;
  if (Array.isArray(value)) return <ChipList items={value} />;
  if (typeof value === 'string') {
    return value.replace(/(\d)_(?=\d)/g, '$1-').replace(/_/g, ' ');
  }
  return value;
};

const getExtraFields = (obj, knownKeys) => {
  if (!obj) return [];
  return Object.entries(obj).filter(([key, value]) => {
    if (knownKeys.includes(key)) return false;
    if (value == null || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });
};

const truncateName = (name, max = 25) => {
  if (!name) return '—';
  if (name.length <= max) return name;
  const dotIdx = name.lastIndexOf('.');
  const ext = dotIdx !== -1 ? name.slice(dotIdx) : '';
  return name.slice(0, max - ext.length - 3) + '...' + ext;
};

const isImageFile = (report) => {
  if (report.file_type && report.file_type.startsWith('image/')) return true;
  const ext = (report.file_name || '').split('.').pop().toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
};

const isPdfFile = (report) => {
  if (report.file_type === 'application/pdf') return true;
  return (report.file_name || '').split('.').pop().toLowerCase() === 'pdf';
};

const NriUserDetail = (props) => {
  const userId = props.match.params.id;
  const [overview, setOverview] = useState(null);
  const [reports, setReports] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [tasks, setTasks] = useState(null);
  const [weekTasks, setWeekTasks] = useState(null);
  const [modalReport, setModalReport] = useState(null);
  const [deleteReportId, setDeleteReportId] = useState(null);
  const viewerRef = useRef(null);

  const handleFullscreen = () => {
    const el = viewerRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };

  const handleDeleteReport = async (reportId) => {
    props.toggleLoader(true);
    await deleteMaternityReport(userId, reportId);
    setReports((prev) => prev.filter((r) => r._id !== reportId));
    setDeleteReportId(null);
    props.toggleLoader(false);
  };

  useEffect(() => {
    const fetchAll = async () => {
      props.toggleLoader(true);
      const [ovRes, rpRes, apRes, tkRes, wkRes] = await Promise.all([
        getMaternityUserOverview(userId),
        getMaternityUserReports(userId),
        getMaternityUserAppointments(userId),
        getMaternityUserTasks(userId),
        getMaternityUserWeekTasks(userId),
      ]);
      setOverview(ovRes?.data || null);
      setReports(Array.isArray(rpRes?.data) ? rpRes.data : []);
      setAppointments(Array.isArray(apRes?.data) ? apRes.data : []);
      setTasks(tkRes?.data || null);
      setWeekTasks(wkRes?.data || null);
      props.toggleLoader(false);
    };


    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const user = overview?.user;

  // console.log('User Overview:', overview);
  const onboarding = overview?.onboarding;
  const journey = overview?.journey;
  const pregnancyProfile = overview?.pregnancy_profile;
  const fullName = user
    ? (user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '—')
    : '—';
  const avatarColor = colorForName(fullName);
  const initials = initialsOf(fullName);

  return (
    <>
      <body className="g-sidenav-show bg-gray-100">
        <div id="wrapper">
          <Leftbar />
          <main className="main-content border-radius-lg">
            <Topbar />

           
               {/* <div className='mt-10'/> */}
            <div className="container-fluid mt-1">
              <div className="ud-back-row ">
                <Link to="/users" className="ud-back-btn">
                  <i className="fas fa-arrow-left" /> Back to Users
                </Link>
              </div>

              {/* ===== Section 1: User Full Overview ===== */}
              <div className="ud-page-section">
                <h4 className="ud-page-section-title">
                  <i className="fas fa-user-circle" /> User Full Overview
                </h4>
                <div className="ud-grid-2">
                  {/* Profile */}
                  <SectionCard title="Profile" icon="fa-id-card">
                    {user ? (
                      <>
                        <div className="ud-profile-block">
                          <div className="ud-profile-avatar" style={{ background: avatarColor }}>
                            {user.photo_url ? (
                              <img src={user.photo_url} alt={fullName} />
                            ) : (
                              <span>{initials}</span>
                            )}
                          </div>
                          <div className="ud-profile-name">{fullName}</div>
                          <div className="ud-profile-sub">Joined {fmtDate(user.createdAt)}</div>
                        </div>
                        <div className="ud-info-grid mt-3">
                          <InfoRow label="Email" value={user.email} />
                          <InfoRow label="Phone" value={user.phone_number} />
                          <InfoRow label="Registered At" value={fmtDateTime(user.createdAt)} />
                          <InfoRow label="Registered Platform" value={user.platform} />
                          <InfoRow label="Last Login At" value={fmtDateTime(user.last_login_at)} />
                          <InfoRow label="Last Login Platform" value={user.last_login_platform} />
                        </div>
                      </>
                    ) : (
                      <EmptyState message="No profile data available" />
                    )}
                  </SectionCard>

                  {/* Onboarding */}
                  <SectionCard title="Onboarding" icon="fa-tasks">
                    {onboarding ? (
                      <div className="ud-info-grid">
                        <InfoRow label="Completed" value={<BoolBadge value={onboarding.is_completed} />} />
                        <InfoRow label="Pregnancy Profile Created" value={<BoolBadge value={onboarding.pregnancy_profile_created} />} />
                        <InfoRow label="First Report Uploaded" value={<BoolBadge value={onboarding.first_report_uploaded} />} />
                        <InfoRow label="First Appointment Added" value={<BoolBadge value={onboarding.first_appointment_added} />} />
                        <InfoRow label="Steps Completed" value={fmtList(onboarding.steps_completed)} />
                        <InfoRow label="Completed At" value={fmtDateTime(onboarding.completed_at)} />
                      </div>
                    ) : (
                      <EmptyState message="No onboarding data available" />
                    )}
                  </SectionCard>

                  {/* Journey */}
                  <SectionCard title="Journey" icon="fa-route">
                    {journey ? (
                      <div className="ud-info-grid">
                        <InfoRow label="For Whom" value={journey.for_whom?.replace(/_/g, ' ')} />
                        <InfoRow label="Person Name" value={journey.person_name} />
                        <InfoRow label="Person Age" value={journey.person_age} />
                        <InfoRow label="Journey Stage" value={journey.journey_stage?.replace(/_/g, ' ')} />
                        <InfoRow label="LMP Date" value={fmtDate(journey.lmp_date)} />
                        <InfoRow label="Due Date" value={fmtDate(journey.due_date || pregnancyProfile?.due_date)} />
                        <InfoRow label="Cycle Length" value={journey.cycle_length ? `${journey.cycle_length} days` : null} />
                        <InfoRow label="Consulting Doctor" value={journey.consulting_doctor?.replace(/_/g, ' ')} />
                        <InfoRow label="Current Symptoms" value={<ChipList items={journey.current_symptoms} />} />
                        <InfoRow label="Help Preferences" value={<ChipList items={journey.help_preferences} />} />
                        {getExtraFields(journey, JOURNEY_KNOWN_KEYS).map(([key, value]) => (
                          <InfoRow key={key} label={formatFieldLabel(key)} value={formatFieldValue(value)} />
                        ))}
                        <InfoRow label="Completed" value={<BoolBadge value={journey.is_completed} />} />
                        <InfoRow label="Completed At" value={fmtDateTime(journey.completed_at)} />
                      </div>
                    ) : (
                      <EmptyState message="No journey data available" />
                    )}
                  </SectionCard>

                  {/* Pregnancy Profile */}
                  <SectionCard title="Pregnancy Profile" icon="fa-baby">
                    {pregnancyProfile ? (
                      <div className="ud-info-grid">
                        <InfoRow label="LMP Date" value={fmtDate(pregnancyProfile.lmp_date)} />
                        <InfoRow label="Due Date" value={fmtDate(pregnancyProfile.due_date)} />
                        <InfoRow label="Current Week" value={pregnancyProfile.current_week != null ? `Week ${pregnancyProfile.current_week}` : null} />
                        <InfoRow label="First Pregnancy" value={<BoolBadge value={pregnancyProfile.is_first_pregnancy} />} />
                        <InfoRow label="Previous Pregnancies" value={pregnancyProfile.number_of_previous_pregnancies} />
                        <InfoRow label="Blood Group" value={pregnancyProfile.blood_group} />
                        <InfoRow label="Cycle Length" value={pregnancyProfile.cycle_length ? `${pregnancyProfile.cycle_length} days` : null} />
                        <InfoRow label="Consulting Doctor" value={pregnancyProfile.consulting_doctor?.replace(/_/g, ' ')} />
                        <InfoRow label="Current Symptoms" value={<ChipList items={pregnancyProfile.current_symptoms} />} />
                        <InfoRow label="Help Preferences" value={<ChipList items={pregnancyProfile.help_preferences} />} />
                        <InfoRow label="Pre-existing Conditions" value={fmtList(pregnancyProfile.pre_existing_conditions)} />
                        <InfoRow label="Active" value={<BoolBadge value={pregnancyProfile.is_active} />} />
                      </div>
                    ) : (
                      <EmptyState message="No pregnancy profile data available" />
                    )}
                  </SectionCard>
                </div>
              </div>

              {/* ===== Section 2: Reports ===== */}
              <div className="ud-page-section">
                <h4 className="ud-page-section-title">
                  <i className="fas fa-file-medical" /> Reports
                </h4>
                {reports.length > 0 ? (
                  <div className="ud-grid-3">
                    {reports.map((report) => {
                      const typeColor = reportTypeColors[report.report_type] || '#6b7088';
                      const isImg = isImageFile(report);
                      const isPdf = isPdfFile(report);
                      const pendingDelete = deleteReportId === report._id;
                      return (
                        <div className="ud-report-card" key={report._id}>
                          <div className="ud-report-header">
                            <span
                              className="ud-report-type-badge"
                              style={{ background: typeColor + '20', color: typeColor }}
                            >
                              {(report.report_type || 'other').replace(/_/g, ' ')}
                            </span>
                            <div className="ud-report-header-right">
                              <span className="ud-report-category">{report.category}</span>
                              {/* <button
                                className="ud-report-delete-btn"
                                onClick={() => setDeleteReportId(pendingDelete ? null : report._id)}
                                title="Delete report"
                              >
                                <i className="fas fa-trash" />
                              </button> */}
                            </div>
                          </div>

                          <div className="ud-report-title">{report.title || '—'}</div>
                          {report.description && (
                            <div className="ud-report-desc">{report.description}</div>
                          )}

                          {report.file_url && (
                            <div
                              className="ud-report-preview"
                              onClick={() => setModalReport(report)}
                              title="Click to view"
                            >
                              {isImg ? (
                                <img src={report.file_url} alt={report.title || 'report'} className="ud-report-thumb" />
                              ) : isPdf ? (
                                <div className="ud-report-pdf-icon">
                                  <i className="fas fa-file-pdf" />
                                  <span>PDF</span>
                                </div>
                              ) : (
                                <div className="ud-report-file-icon">
                                  <i className="fas fa-file-alt" />
                                  <span>File</span>
                                </div>
                              )}
                              <div className="ud-report-preview-overlay">
                                <i className="fas fa-eye" /> View
                              </div>
                            </div>
                          )}

                          <div className="ud-info-grid mt-2">
                            {report.doctor_name && <InfoRow label="Doctor" value={report.doctor_name} />}
                            <InfoRow label="Date" value={fmtDate(report.report_date)} />
                            <InfoRow label="File" value={truncateName(report.file_name)} />
                            {report.file_size != null && (
                              <InfoRow label="Size" value={`${Math.round(report.file_size / 1024)} KB`} />
                            )}
                          </div>

                          {pendingDelete && (
                            <div className="ud-report-confirm">
                              <span>Delete this report?</span>
                              <div className="ud-report-confirm-actions">
                                <button
                                  className="ud-confirm-yes"
                                  onClick={() => handleDeleteReport(report._id)}
                                >
                                  Yes, delete
                                </button>
                                <button
                                  className="ud-confirm-no"
                                  onClick={() => setDeleteReportId(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="ud-section-card">
                    <div className="ud-section-body">
                      <EmptyState message="No reports uploaded yet" />
                    </div>
                  </div>
                )}
              </div>

              {/* ===== Section 3: Appointments ===== */}
              <div className="ud-page-section">
                <h4 className="ud-page-section-title">
                  <i className="fas fa-calendar-check" /> Appointments
                </h4>
                {appointments.length > 0 ? (
                  <div className="ud-grid-2">
                    {appointments.map((appt) => (
                      <div className="ud-appt-card" key={appt._id}>
                        <div className="ud-appt-header">
                          <div className="ud-appt-title">{appt.title || '—'}</div>
                          <StatusBadge value={appt.status} colorMap={appointmentStatusColors} />
                        </div>
                        <div className="ud-info-grid mt-2">
                          <InfoRow label="Type" value={appt.appointment_type?.replace(/_/g, ' ')} />
                          <InfoRow label="Date" value={fmtDate(appt.appointment_date)} />
                          <InfoRow label="Time" value={appt.appointment_time} />
                          {appt.doctor_name && <InfoRow label="Doctor" value={appt.doctor_name} />}
                          {appt.location && <InfoRow label="Location" value={appt.location} />}
                          {appt.week_number != null && <InfoRow label="Week" value={`Week ${appt.week_number}`} />}
                          {appt.notes && <InfoRow label="Notes" value={appt.notes} />}
                          <InfoRow label="Reminder" value={<BoolBadge value={appt.reminder_enabled} />} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ud-section-card">
                    <div className="ud-section-body">
                      <EmptyState message="No appointments added yet" />
                    </div>
                  </div>
                )}
              </div>

              {/* ===== Section 4: Tasks ===== */}
              <div className="ud-page-section">
                {/* <h4 className="ud-page-section-title">
                  <i className="fas fa-check-circle" /> Tasks
                </h4> */}
                {/* <div className="ud-grid-2">
                  <SectionCard title="Appointment Tasks" icon="fa-clipboard-list">
                    {tasks?.appointment_tasks?.length > 0 ? (
                      <div className="ud-task-list">
                        {tasks.appointment_tasks.map((task) => (
                          <div className="ud-task-item" key={task._id}>
                            <span className={`ud-task-check ${task.is_completed ? 'ud-task-done' : ''}`}>
                              <i className={`fas ${task.is_completed ? 'fa-check-circle' : 'fa-circle'}`} />
                            </span>
                            <div className="ud-task-info">
                              <div className="ud-task-title">{task.title}</div>
                              <div className="ud-task-meta">
                                {task.task_type?.replace(/_/g, ' ')} · Due {fmtDate(task.due_date)}
                              </div>
                            </div>
                            <BoolBadge value={task.is_active} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyState message="No appointment tasks" />
                    )}
                  </SectionCard>

                  <SectionCard title="Recent Completions" icon="fa-history">
                    {tasks?.recent_completions?.length > 0 ? (
                      <div className="ud-task-list">
                        {tasks.recent_completions.map((completion) => (
                          <div className="ud-completion-item" key={completion._id}>
                            <i className="fas fa-check-circle ud-completion-icon" />
                            <div className="ud-completion-info">
                              <div className="ud-completion-date">{fmtDate(completion.date)}</div>
                              <div className="ud-completion-id">Task ID: {completion.task_id}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyState message="No recent completions" />
                    )}
                  </SectionCard>
                </div> */}
              </div>

              {/* ===== Section 5: Current Week Tasks ===== */}
              {/* <div className="ud-page-section">
                <h4 className="ud-page-section-title">
                  <i className="fas fa-calendar-week" /> Current Week Tasks
                  {weekTasks?.current_week != null && (
                    <span className="ud-week-chip">
                      Week {weekTasks.current_week}
                      {weekTasks.date && (
                        <span className="ud-week-date"> · {fmtDate(weekTasks.date)}</span>
                      )}
                    </span>
                  )}
                </h4>

                {weekTasks ? (
                  <div className="ud-grid-2">
              
                    <SectionCard title="Global Tasks" icon="fa-list-ul">
                      {weekTasks.global_tasks?.length > 0 ? (
                        <div className="ud-task-list">
                          {weekTasks.global_tasks.map((task) => {
                            const tt = taskTypeColors[task.task_type] || { bg: '#f3f4f8', color: '#6b7088' };
                            const fq = frequencyColors[task.frequency] || { bg: '#f3f4f8', color: '#6b7088' };
                            return (
                              <div className="ud-wt-item" key={task._id}>
                                <span className={`ud-task-check ${task.is_completed ? 'ud-task-done' : ''}`}>
                                  <i className={`fas ${task.is_completed ? 'fa-check-circle' : 'fa-circle'}`} />
                                </span>
                                <div className="ud-task-info">
                                  <div className="ud-task-title">{task.title}</div>
                                  <div className="ud-wt-badges">
                                    <span className="ud-wt-badge" style={{ background: tt.bg, color: tt.color }}>
                                      {(task.task_type || '').replace(/_/g, ' ')}
                                    </span>
                                    {task.frequency && (
                                      <span className="ud-wt-badge" style={{ background: fq.bg, color: fq.color }}>
                                        {task.frequency.replace(/_/g, ' ')}
                                      </span>
                                    )}
                                    {task.applicable_from_week != null && task.applicable_to_week != null && (
                                      <span className="ud-wt-badge ud-wt-week-range">
                                        Wk {task.applicable_from_week}–{task.applicable_to_week}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <EmptyState message="No global tasks for this week" />
                      )}
                    </SectionCard>

                   
                    <SectionCard title="Appointment Tasks" icon="fa-calendar-check">
                      {weekTasks.appointment_tasks?.length > 0 ? (
                        <div className="ud-task-list">
                          {weekTasks.appointment_tasks.map((task) => (
                            <div className="ud-wt-item" key={task._id}>
                              <span className={`ud-task-check ${task.is_completed ? 'ud-task-done' : ''}`}>
                                <i className={`fas ${task.is_completed ? 'fa-check-circle' : 'fa-circle'}`} />
                              </span>
                              <div className="ud-task-info">
                                <div className="ud-task-title">{task.title}</div>
                                <div className="ud-wt-badges">
                                  <span
                                    className="ud-wt-badge"
                                    style={{ background: '#e8f0fe', color: '#2b6cb0' }}
                                  >
                                    appointment
                                  </span>
                                  {task.due_date && (
                                    <span className="ud-wt-badge ud-wt-due">
                                      Due {fmtDate(task.due_date)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <EmptyState message="No appointment tasks for this week" />
                      )}
                    </SectionCard>
                  </div>
                ) : (
                  <div className="ud-section-card">
                    <div className="ud-section-body">
                      <EmptyState message="No week task data available" />
                    </div>
                  </div>
                )}
              </div> */}
            </div>
          </main>
        </div>
      </body>
      {/* ===== File Viewer Modal ===== */}
      {modalReport && (
        <div className="ud-modal-overlay" onClick={() => setModalReport(null)}>
          <div className="ud-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ud-modal-header">
              <span className="ud-modal-title">{truncateName(modalReport.file_name, 40)}</span>
              <div className="ud-modal-actions">
                <button className="ud-modal-btn" onClick={handleFullscreen} title="Full Screen">
                  <i className="fas fa-expand" />
                </button>
                <a
                  href={modalReport.file_url}
                  download={modalReport.file_name}
                  className="ud-modal-btn"
                  title="Download"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="fas fa-download" />
                </a>
                <button
                  className="ud-modal-btn ud-modal-close"
                  onClick={() => setModalReport(null)}
                  title="Close"
                >
                  <i className="fas fa-times" />
                </button>
              </div>
            </div>
            <div className="ud-modal-body" ref={viewerRef}>
              {isImageFile(modalReport) ? (
                <img
                  src={modalReport.file_url}
                  alt={modalReport.title || 'report'}
                  className="ud-modal-img"
                />
              ) : isPdfFile(modalReport) ? (
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(modalReport.file_url)}&embedded=true`}
                  className="ud-modal-pdf"
                  title="PDF Viewer"
                />
              ) : (
                <div className="ud-modal-unsupported">
                  <i className="fas fa-file-alt" />
                  <p>Preview not available for this file type</p>
                  <a href={modalReport.file_url} target="_blank" rel="noreferrer">
                    Open file
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapDispatchToProps = { toggleLoader };
const mapStateToProps = () => ({});
export default connect(mapStateToProps, mapDispatchToProps)(NriUserDetail);
